<template>
    <div class="lam-calendar">
        <div class="uk-grid uk-grid-small uk-margin-bottom">
            <div class="uk-width-1-10">
                <button v-if="can_go_back" type="button" class="uk-button uk-button-small uk-button-link" @click.prevent="go_back">
                    <open-icon glyph="arrow-left" />
                </button>
            </div>
            <div class="uk-width-8-10 uk-text-center">
                <h3>{{ page_month }}</h3>
            </div>
            <div class="uk-width-1-10 uk-text-right">
                <button type="button" class="uk-button uk-button-small uk-button-link" @click.prevent="go_forward">
                    <open-icon glyph="arrow-right" />
                </button>
            </div>
        </div>
        <div class="lam-calendar-month">
            <div class="uk-flex lam-calendar-week-header">
                <div v-for="day in days" :key="day" class="lam-calendar-day">
                    {{ day }}
                </div>
            </div>
            <div v-for="(days, index) in weeks" :key="index" class="uk-flex lam-calendar-week">
                <div v-for="day in days" :key="`${day.month}.${day.day}`" class="lam-calendar-day">
                    <div class="lam-square">
                        <div
                            :class="{
                                'lam-today': day.is_today,
                                'lam-holiday': day.holiday||day.weekend,
                                'lam-unconfirmed': unconfirmed(day.date),
                                'lam-confirmed': confirmed(day.date),
                                'lam-cancelled': cancelled(day.date),
                                'uk-disabled': day.holiday||day.weekend||day.out_of_range
                            }"
                            @click="edit(day)"
                            class="lam-calendar-date"
                        >
                            <h3>{{ day.day }}</h3>
                            <h6 v-if="day.month">{{ day.month }}</h6>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/javascript">
import OpenIcon from "./OpenIcon"

export default {
    components: {
        OpenIcon
    },
    methods: {
        start_of_week (date) {
            if(this.isoWeek && (date.day() === 1)) return this.$moment(date)
            if(!this.isoWeek && (date.day() === 0)) return this.$moment(date)

            const start = this.$moment(date).startOf("isoWeek")
            if(!this.isoWeek) start.subtract(1, "days")
            return start
        },
        event (date) {
            if(!this.events || !this.events.length) return null
            return this.events.find(event => {
                return date.isSameOrAfter(this.$moment(event.starts_at || event.requested_starts_at)) && date.isSameOrBefore(this.$moment(event.ends_at || event.requested_ends_at))
            })
        },
        holiday (date) {
            if(!this.holidays || !this.holidays.length) return false
            return !!this.holidays.find(holiday => {
                return date.isSameOrAfter(holiday.starts_at) && date.isSameOrBefore(holiday.ends_at)
            })
        },
        weekend (date) {
            return (date.day() === 0) || (date.day() === 6)
        },
        unconfirmed (date) {
            const event = this.event(date)
            if(!event) return false
            return event.unconfirmed
        },
        confirmed (date) {
            const event = this.event(date)
            if(!event) return false
            return event.confirmed
        },
        cancelled (date) {
            const event = this.event(date)
            if(!event) return false
            return event.cancelled
        },
        edit (day) {
            if(!day.out_of_range) this.$emit("click", day.date, this.event(day.date))
        },
        go_back () {
            const start = this.page_start.subtract(1, "months")
            this.state.page_start = this.$moment.max(start, this.start).format("YYYY-MM-DD")
        },
        go_forward () {
            this.state.page_start = this.page_start.add(1, "months").format("YYYY-MM-DD")
        }
    },
    computed: {
        can_go_back () {
            return !this.page_start.isSameOrBefore(this.start)
        },
        page_start () {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            if(!this.state.page_start) this.state.page_start = this.start.format("YYYY-MM-DD")
            return this.$moment(this.state.page_start)
        },
        page_month () {
            return this.page_start.format("MMM YYYY")
        },
        start () {
            let start = this.$moment(this.minDate || new Date())
            if(!this.minDate) start = start.startOf("month")
            return start.startOf("day")
        },
        days () {
            const date = this.start_of_week(this.start)
            const end = this.$moment(date).add(6, "days")
            const days = []
            while(date.isSameOrBefore(end)) {
                days.push(date.format("ddd"))
                date.add(1, "days")
            }
            return days
        },
        weeks () {
            // [[{date, day, month, active},...], [...], [...], ....]

            const weeks = []
            const start = this.page_start
            let end = this.$moment(this.maxDate || new Date()).endOf("day")
            const calendar_end = this.$moment.max([
                this.$moment(start).endOf("month").endOf("day"), // end of the month
                this.$moment(start).add(4, "weeks").endOf("week").subtract(1, "days").endOf("day"), // minimum 4 weeks displayed
                end // maxDate
            ])
            end = this.maxDate ? this.$moment(this.maxDate) : calendar_end
            let current_month = ""
            const calendar_start = this.start_of_week(start)
            const today = this.$moment()

            while(calendar_start.isSameOrBefore(calendar_end)) {
                const week = []

                let week_date = this.$moment(calendar_start)
                const week_end = this.$moment(week_date).add(6, "days").endOf("day")
                while(week_date.isSameOrBefore(week_end)) {
                    let month = week_date.format("MMM")
                    if(month === current_month) month = ""
                    else current_month = month

                    const out_of_range = week_date.isBefore(start) || week_date.isAfter(end) // || (week_date.isAfter(start) && !!month)

                    week.push({
                        date: week_date,
                        day: week_date.format("D"),
                        month: month,
                        holiday: this.holiday(week_date),
                        weekend: this.weekend(week_date),
                        out_of_range: out_of_range,
                        is_today: week_date.isSame(today, "day")
                    })
                    week_date = this.$moment(week_date).add(1, "days")
                }

                weeks.push(week)
                calendar_start.add(1, "weeks")
            }

            return weeks
        }
    },
    props: {
        events: {
            type: Array,
            default: () => {
                return []
            }
        },
        holidays: {
            type: Array,
            default: () => {
                return []
            }
        },
        startDate: {
            default: null
        },
        minDate: {
            default: null
        },
        maxDate: {
            default: null
        },
        isoWeek: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            state: {
                page_start: null
            }
        }
    }
}
</script>
